import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { encryptData } from "../../utils/localEncruption";
import { decryptData, decryptAPIData } from "../../utils/localEncruption";

const ENCRYPTION_KEY = "gsDFQwTwuuOXWgh0JG/En4NtZWbahbacdQtlCkMg9tE=";

const fetchSavingsFromAPI = async () => {
  const response = await fetch(
    `${process.env.REACT_APP_SAVINGS_RATES_URL_ALL}`
  );
  const encryptedData = await response.text();
  try {
    const decryptedData = await decryptAPIData(encryptedData, ENCRYPTION_KEY);
    return decryptedData;
  } catch (error) {
    console.error("Decryption failed for FD rate:", error);
  }

  return response.json();
};

// Phase 2: Fetch a single bank's savings data from the new endpoint
const fetchSingleBankFromAPI = async (seoName) => {
  const baseUrl = process.env.REACT_APP_VIKALP || "http://127.0.0.1:8000";
  const response = await fetch(`${baseUrl}/sd-bank-details/${seoName}/`);
  if (!response.ok) {
    throw new Error(`SD bank details fetch failed: ${response.status}`);
  }
  const encryptedData = await response.text();
  try {
    const decryptedData = await decryptAPIData(encryptedData, ENCRYPTION_KEY);
    return decryptedData;
  } catch (error) {
    console.error("Decryption failed for single bank SD:", error);
  }
  return response.json();
};

export const fetchUsingAsyncAwaitWithFetchApi = async (thunkAPI) => {
  const state = thunkAPI.getState();
  const response = await fetch(
    `${process.env.REACT_APP_SAVINGS_RATES_URL}` + state.savingsFullRate.Amount,
    { mode: "cors" }
  );
  const data = await response.text();
  try {
    const decryptedData = await decryptAPIData(data, ENCRYPTION_KEY);
    return decryptedData;
  } catch (error) {
    console.error("Decryption failed for the second API:", error);
  }
};

// Helper function to get data from local storage
export const getSavingsFromLocalStorage = () => {
  const storedData = localStorage.getItem("savingsData");
  const salt = `${process.env.REACT_APP_SALT}`;
  if (!storedData) {
    return null;
  }

  try {
    const decryptedData = decryptData(storedData, salt);
    //
    return decryptedData;
  } catch (error) {
    return null;
  }
};

// Helper function to save data to local storage
const saveSavingsToLocalStorage = (data) => {
  const salt = `${process.env.REACT_APP_SALT}`;
  const encryptedData = encryptData(data, salt);
  localStorage.setItem("savingsData", encryptedData);
};

// Helper function to get the timestamp when data was last fetched
const getTimestampFromLocalStorage = (key) => {
  return localStorage.getItem(`${key}_timestamp`);
};

// Helper function to save the timestamp when data is fetched
const saveTimestampToLocalStorage = (key) => {
  const timestamp = new Date().getTime();
  localStorage.setItem(`${key}_timestamp`, timestamp);
};

// Phase 1: Real cache threshold — 5 minutes. Returning users within this
// window get instant render from localStorage; background revalidation
// still happens via backgroundFetchAllSavings.
const DATA_FRESHNESS_THRESHOLD = 5 * 60 * 1000; // 5 minutes

// Async thunk to fetch savings, considering local storage
export const fetchSavings = createAsyncThunk(
  "fetchSavings",
  async (_, thunkAPI) => {
    const cachedData = getSavingsFromLocalStorage();
    const timestamp = getTimestampFromLocalStorage("savingsData");
    const currentTime = new Date().getTime();

    // Check if data in local storage is stale
    const isData1Stale =
      !timestamp || currentTime - timestamp >= DATA_FRESHNESS_THRESHOLD;

    if (!cachedData || isData1Stale) {
      try {
        const data = await fetchSavingsFromAPI();

        saveSavingsToLocalStorage(data);
        saveTimestampToLocalStorage("savingsData");

        return data;
      } catch (error) {
        if (cachedData) {
          console.warn("Savings API fetch failed, using stale cached data as fallback");
          return cachedData;
        }

        return thunkAPI.rejectWithValue(error.message);
      }
    }
    return cachedData;
  }
);

// Phase 2: Fetch a single bank's SD data (fast path for detail pages)
export const fetchSingleSavingsBank = createAsyncThunk(
  "savingsFullRate/fetchSingleBank",
  async (seoName, thunkAPI) => {
    try {
      const data = await fetchSingleBankFromAPI(seoName);
      return { seoName, bankData: data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Phase 1: Background fetch all savings — silent revalidation, no spinner.
// Used by detail pages to prefetch data for RelatedBanks after the single
// bank data has already loaded.
export const backgroundFetchAllSavings = createAsyncThunk(
  "savingsFullRate/backgroundFetchAll",
  async (_, thunkAPI) => {
    try {
      const data = await fetchSavingsFromAPI();
      saveSavingsToLocalStorage(data);
      saveTimestampToLocalStorage("savingsData");
      return data;
    } catch (error) {
      // Silently fail — this is a background enhancement, not critical
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchOtherData = createAsyncThunk(
  "savings/fetchOtherData",
  async (_, thunkAPI) => {
    try {
      const data = await fetchUsingAsyncAwaitWithFetchApi(thunkAPI);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const savingsFullRatesSlice = createSlice({
  name: "savingsFullRate",
  initialState: {
    isLoading: false,
    isSingleBankLoading: false, // Phase 2: loading state for single bank fetch
    isBackgroundRefreshing: false, // Phase 1: silent background refresh (no spinner)
    data: getSavingsFromLocalStorage() || {},
    bankData: [],
    selectedFilters: [],
    isError: false,
    Amount: "1000000",
    selectedBanks: [],
    sortedItems: [],
  },

  reducers: {
    toggleFilter: (state, action) => {
      const selectedCategory = action.payload;
      if (state.selectedFilters.includes(selectedCategory)) {
        state.selectedFilters = state.selectedFilters.filter(
          (element) => element !== selectedCategory
        );
      } else {
        state.selectedFilters.push(selectedCategory);
      }
    },
    resetFilters: (state) => {
      state.selectedFilters = [];
    },
    selectAllFilters: (state, action) => {
      state.selectedFilters = action.payload;
    },

    setAmount: (state, action) => {
      state.Amount = action.payload; // Add a reducer for setting amount
    },

    setSelectedBanks: (state, action) => {
      state.selectedBanks = action.payload;
    },

    setSortedItems: (state, action) => {
      state.sortedItems = action.payload;
    },
  },

  extraReducers: (builder) => {
    // Handle fetching savings data (all banks)
    builder.addCase(fetchSavings.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(fetchSavings.fulfilled, (state, action) => {
      state.isLoading = false;
      state.data = action.payload; // result of data return by dispatch(fetchSavings());
    });
    builder.addCase(fetchSavings.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
    });

    // Phase 2: Handle single bank fetch
    builder.addCase(fetchSingleSavingsBank.pending, (state) => {
      state.isSingleBankLoading = true;
    });
    builder.addCase(fetchSingleSavingsBank.fulfilled, (state, action) => {
      state.isSingleBankLoading = false;
      const { seoName, bankData } = action.payload;
      // Merge single bank data into the data object
      state.data = { ...state.data, [seoName]: bankData };
    });
    builder.addCase(fetchSingleSavingsBank.rejected, (state, action) => {
      state.isSingleBankLoading = false;
      // Don't set isError — the component will fall back to fetchSavings
    });

    // Phase 1: Handle background fetch (no spinner, silent update)
    builder.addCase(backgroundFetchAllSavings.pending, (state) => {
      state.isBackgroundRefreshing = true;
    });
    builder.addCase(backgroundFetchAllSavings.fulfilled, (state, action) => {
      state.isBackgroundRefreshing = false;
      state.data = action.payload;
    });
    builder.addCase(backgroundFetchAllSavings.rejected, (state) => {
      state.isBackgroundRefreshing = false;
      // Silently fail — existing data remains intact
    });

    // Handle fetchOtherData
    builder.addCase(fetchOtherData.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchOtherData.fulfilled, (state, action) => {
      state.isLoading = false;
      state.bankData = action.payload;
    });
    builder.addCase(fetchOtherData.rejected, (state, action) => {
      console.error("Error", action.payload);
      state.isError = true;
    });
  },
});

export const {
  toggleFilter,
  resetFilters,
  selectAllFilters,
  setAmount,
  setSelectedBanks,
  setSortedItems,
} = savingsFullRatesSlice.actions;
export default savingsFullRatesSlice.reducer;
